/**
 * OSSignupModal
 *
 * Opens the RiverOS v1 purchase flow as a portal overlay so users don't
 * lose their current workspace context. The modal covers the purchase step;
 * after the Stripe redirect and payment the user lands on the existing full-page
 * /os-signup?step=local-complete view which shows their license key.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock } from '@phosphor-icons/react';
import { useOSSignupModalStore } from '../../stores/osSignupModalStore';
import { API_BASE_URL } from '../../api/config';
import { createFileLogger } from '../../utils/logger';
import fetchService from '../../services/fetchService';
import LaptopHero from '../../pages/marketing/products/PersonalAIPage/LaptopHero';
import './OSSignupModal.scss';

const log = createFileLogger('OS_SIGNUP_MODAL');

const OSSignupModal: React.FC = () => {
  const { isOpen, close } = useOSSignupModalStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handlePurchase = async () => {
    setIsProcessing(true);
    setError('');
    log.log('[OSSignupModal] Starting RiverOS v1 purchase...');

    try {
      // Get user email from stored token
      let email = '';
      const idToken = localStorage.getItem('id_token');
      if (idToken) {
        const { parseJwtPayload } = await import('../../utils/jwtUtils');
        const payload = parseJwtPayload(idToken);
        email = payload?.email || '';
      }

      if (!email) {
        setError('Unable to determine your email. Please refresh and try again.');
        setIsProcessing(false);
        return;
      }

      log.log('[OSSignupModal] Creating checkout session for', email);

      const data = await fetchService(`${API_BASE_URL}/os-signup/purchase-riveros-v1`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          success_url: `${window.location.origin}/os-signup?step=local-complete&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/os-signup?step=local-purchase`
        })
      });

      if (data.url) {
        log.log('[OSSignupModal] Opening Stripe checkout');
        if (window.riverOS?.openExternal) {
          // Electron: open in the system browser so the app stays intact
          await window.riverOS.openExternal(data.url);
          setIsProcessing(false);
        } else {
          // Web browser: open in a new tab
          window.open(data.url, '_blank', 'noopener,noreferrer');
          setIsProcessing(false);
        }
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      log.error('[OSSignupModal] Purchase error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  const content = (
    <div className="os-signup-modal-overlay" onClick={close}>
      <div className="os-signup-modal-panel" onClick={(e) => e.stopPropagation()}>
        <button className="os-signup-modal-close" onClick={close} title="Close">
          <X size={18} weight="bold" />
        </button>

        <div className="os-modal-laptop-hero">
          <LaptopHero />
        </div>

        <div className="os-signup-modal-body">
          <div className="os-step-header">
            <h2>RiverOS: your personal, private AI system</h2>
            <p>One-time license - your data, your hardware, your AI</p>
          </div>

          {error && (
            <div className="purchase-error">
              {error}
            </div>
          )}

          <div className="purchase-summary">
            <div className="summary-header">
              <h3>What's included</h3>
              <div className="price">$299</div>
            </div>

            <ul className="feature-list">
              <li>Full self-hosted River</li>
              <li>Local AI models via Ollama — no cloud, no per-token fees</li>
              <li>All tools and agents running on your hardware</li>
              <li>Complete data privacy — nothing leaves your machine</li>
              <li>One device, one-time payment</li>
              <li>Discount on our future River 1 AI hardware</li>
            </ul>
          </div>

          <div className="stripe-checkout-section">
            <button
              className="os-btn os-btn-primary"
              onClick={handlePurchase}
              disabled={isProcessing}
            >
              {isProcessing ? 'Redirecting to checkout...' : 'Get RiverOS'}
            </button>
            <p className="secure-note">
              <Lock size={13} weight="duotone" /> Secure payment via Stripe · 
              Your license key is emailed immediately after purchase
            </p>
          </div>

          <div className="already-purchased">
            <p>
              Already purchased? Your license key is in{' '}
              <button
                className="already-purchased-link"
                onClick={() => {
                  close();
                  window.dispatchEvent(new CustomEvent('openSettings', { detail: { tab: 'account', sectionId: 'license-section' } }));
                }}
              >
                Settings → License
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default OSSignupModal;
